import React, { useEffect, useState } from 'react';
import { Card, Icon, Modal, Button, Checkbox } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import GridColumnInputWrapper from '@components/common/GridColumnInputWrapper';
import CommonTagsInputWrapper from '@components/common/CommonTagsInputWrapper';
import { personsActions } from '@behavior/persons';
import PersonPopup from '@components/Filtering/OverviewTable/InventorySimplifiedView/Components/PersonPopup';
import styles from './styles/Activity.module.scss';
import { useFormContext } from 'react-hook-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { ContactDTO } from '@behavior/inventories/dto/ContactDTO';
import { ActivityInitialDataResponse } from '../types/ActivityInitialDataResponse';
import { PersonDTO } from '@behavior/inventories/dto/PersonDTO';
import { renderPersonSuggestionAddition } from './PersonSuggestionAddition';

type Props = {
  initialData?: ActivityInitialDataResponse;
  selectedContacts: ContactDTO[];
  setSelectedContacts?: (selectedContact: ContactDTO[]) => void;
  department: string;
  setDepartment?: (department: string) => void;
  disabled?: boolean;
  showPersonalData?: boolean;
};

const ActivityContact = ({
  initialData,
  selectedContacts,
  setSelectedContacts,
  department,
  setDepartment,
  disabled,
  showPersonalData,
}: Props) => {
  const { t } = useTranslation();
  const {
    register,
    clearErrors,
    formState: { errors },
    setValue,
  } = useFormContext();
  const [departmentListSuggestion, setDepartmentListSuggestion] = useState<string[]>([]);
  const isNotMissingCompetencies = false;
  const [clearInputCounter, setClearInputCounter] = useState(0);
  const [blockedWarning, setBlockedWarning] = useState(false);
  const [blockedPerson, setBlockedPerson] = useState<PersonDTO | null>(null);
  const [overrideChecked, setOverrideChecked] = useState(false);

  useEffect(() => {
    if (initialData) {
      setDepartmentListSuggestion(initialData.department);
    }
  }, [initialData]);

  const onDepartmentSelect = (selectedOption: string) => {
    if (setDepartment) {
      setDepartment(selectedOption);
    }
    clearErrors('department');
  };

  useEffect(() => {
    setValue('department', department);
  }, [department]);

  useEffect(() => {
    setValue('selectedContacts', selectedContacts);
  }, [selectedContacts]);

  const closeWarning = () => {
    setBlockedWarning(false);
    setBlockedPerson(null);
    setOverrideChecked(false);
  };

  const getMissingCompetencies = (person: PersonDTO): string => {
    const missing: string[] = [];
    const isExpired = (date: string | undefined | null) => !date || new Date(date) < new Date();
    if (isExpired(person.awarenessExpiryDate)) missing.push('Awareness');
    if (isExpired(person.handlingExpiryDate)) missing.push('Handling');
    return missing.join(' / ');
  };

  const confirmOverride = () => {
    if (blockedPerson) {
      const contactExists = selectedContacts.some(contact => contact.person.id === blockedPerson.id);
      if (!contactExists) {
        const newItem: ContactDTO = {
          id: null,
          person: blockedPerson,
        };
        const updatedList = [...selectedContacts, newItem];
        if (setSelectedContacts) {
          setSelectedContacts(updatedList);
        }
        if (updatedList.length > 0) {
          clearErrors('selectedContacts');
        }
      }
    }
    closeWarning();
  };

  const onContactSelect = (selectedOption: PersonDTO) => {
    if (selectedOption.isMissingCompetencies) {
      setBlockedPerson(selectedOption);
      setBlockedWarning(true);
      clearNewContact();
      return;
    }
    const contactExists = selectedContacts.some(contact => contact.person.id === selectedOption.id);

    if (!contactExists) {
      const newItem: ContactDTO = {
        id: null,
        person: selectedOption,
      };
      const updatedList = [...selectedContacts, newItem];
      if (setSelectedContacts) {
        setSelectedContacts(updatedList);
      }
      if (updatedList.length > 0) {
        clearErrors('selectedContacts');
      }
    }
    clearNewContact();
  };

  const clearNewContact = () => {
    setClearInputCounter(clearInputCounter + 1);
  };

  const onDeleteIcon = (contact: PersonDTO) => {
    const updatedContacts = selectedContacts.filter(c => c.person.id !== contact.id);
    if (setSelectedContacts) {
      setSelectedContacts(updatedContacts);
    }
  };

  const renderSelectedContacts = () => {
    return (
      <div className={styles.personListDiv}>
        {selectedContacts.map((contact, index) => (
          <div className={disabled ? `${styles.disabledContactList}` : ''} key={index}>
            <PersonPopup
              contact={contact.person}
              closeIcon={!disabled}
              onDelete={() => onDeleteIcon(contact.person)}
              showPersonalData={showPersonalData}
              showWarning={contact.person?.isMissingCompetencies}
              showWarningIfPersonNotAtCern
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className={styles.contactCard}>
      <div className={`${styles.activityTextHeader} ${styles.contactHeader}`}>
        {t('activity.details.page.activity.responsible.container.title')}
      </div>
      <div className={`${styles.basicDiv} ${styles.contactDiv}`}>
        <div className={styles.myContainer}>
          <span className={styles.spanStyle}>
            {t('activity.details.page.components.activity.department.add.label')}
          </span>
          <div className={styles.departmentContainer}>
            <GridColumnInputWrapper
              {...register('department', {
                required: t('activity.form.error.department.required'),
              })}
              name="department"
              specialRequiredCondition
              disabled={disabled}
              render={(props: any) => (
                <>
                  <div className={styles.errorMessage}>
                    {errors?.department?.message?.toString()}
                  </div>
                  <CommonTagsInputWrapper
                    {...props}
                    className={styles.departmentField}
                    suggestionList={departmentListSuggestion}
                    value={department}
                    maxTags={1}
                    onlyUnique
                    placeholder="activity.details.page.components.activity.department.add.placeholder"
                    onSelect={onDepartmentSelect}
                  />
                </>
              )}
            />
          </div>
        </div>
        {!disabled && (
          <>
            <div className={styles.myContainer}>
              <span className={styles.spanStyle}>
                {t('activity.details.page.components.activity.contact.add.label')}
              </span>
              <div className={styles.departmentContainer}>
                <GridColumnInputWrapper
                  {...register('selectedContacts', {
                    validate: value => {
                      if (!value || !Array.isArray(value) || value.length < 1) {
                        return t('activity.form.error.contacts.required');
                      }
                      return true;
                    },
                  })}
                  render={(props: any) => (
                    <>
                      <div className={styles.errorMessage}>
                        {errors?.selectedContacts?.message?.toString()}
                      </div>
                      <CommonTagsInputWrapper
                        name={'newPerson.person'}
                        dynamicSuggestionListAction={(v: any) =>
                          personsActions.getPersonAutocompleteListAction(v, isNotMissingCompetencies)
                        }
                        matchProperty={'name'}
                        displayProps={'name'}
                        maxTags={1}
                        onlyUnique
                        clearInputCounter={clearInputCounter}
                        disabled={disabled}
                        placeholder="activity.details.page.components.activity.contact.add.placeholder"
                        className={styles.contactInput}
                        onSelect={onContactSelect}
                        renderSuggestionAddition={(obj: any) =>
                          renderPersonSuggestionAddition(obj, t)
                        }
                      />
                    </>
                  )}
                />
              </div>
            </div>
          </>
        )}

        {selectedContacts?.length > 0 && renderSelectedContacts()}
      </div>

      <Modal
        open={blockedWarning}
        onClose={closeWarning}
        size="tiny"
      >
        <Modal.Header>
          <Icon name="warning sign" color="yellow" />
          {t('message.person.missing.competencies')}
        </Modal.Header>
        <Modal.Content>
          <p>{t('message.person.missing.competencies.cannot.add')}</p>
          <Checkbox
            label={t('message.person.missing.competencies.override', {
              competencies: blockedPerson ? getMissingCompetencies(blockedPerson) : '',
            })}
            checked={overrideChecked}
            onChange={(_: any, data: { checked: any; }) => setOverrideChecked(!!data.checked)}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeWarning}>
            {t('cancel.button')}
          </Button>
          <Button color="blue" onClick={confirmOverride} disabled={!overrideChecked}>
            OK
          </Button>
        </Modal.Actions>
      </Modal>
    </Card>
  );
};

const mapStateToProps = (state: any) => ({
  showPersonalData: state.dataPrivacy.showPersonalData,
});

export default compose(connect(mapStateToProps, null))(ActivityContact);
